'use client';

import { fetchGoodsList } from '@/api/product';
import { fetchBlockValue } from '@/api/public';
import { IProductListApiParams, IProductListApiResponse } from '@/api/types/product';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { handleGoodsListSeo } from '@/utils/goods-list-utils';
import { useCallback, useEffect, useState } from 'react';

interface PageData {
  seo_data?: any;
  product_list?: {
    id: number;
    [key: string]: any;
  };
  [key: string]: any;
}

interface InitialData {
  pageData: PageData | null;
  renderInServer: boolean;
  seoInfo: any;
  seoData: any;
  loading: boolean;
  error: string | null;
}

interface ServerInitialData {
  pageData: PageData | null;
  seoInfo: any;
  seoData: any;
  error: string | null;
}

/**
 * 获取镜片专题页初始数据的Hook（支持服务端数据初始化）
 * @param key - 用于获取页面数据的key，例如 'photochromic_page'
 * @param serverData - 可选的服务端预获取数据
 */
export function useGetInitialData(key: string, serverData?: ServerInitialData) {
  const [data, setData] = useState<InitialData>(() => {
    // 如果有服务端数据，直接使用，避免重复请求
    if (serverData) {
      return {
        pageData: serverData.pageData,
        renderInServer: true,
        seoInfo: serverData.seoInfo,
        seoData: serverData.seoData,
        loading: false,
        error: serverData.error,
      };
    }

    return {
      pageData: null,
      renderInServer: false,
      seoInfo: null,
      seoData: null,
      loading: true,
      error: null,
    };
  });

  const currency = useStore((state) => state.currency);

  const fetchInitialData = useCallback(async () => {
    if (!key) {
      setData((prev) => ({ ...prev, loading: false, error: '缺少必要的key参数' }));
      return;
    }

    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      // 1. 获取页面配置数据
      const blockResponse = await fetchBlockValue({ keys: [key] });

      if (!blockResponse || !blockResponse.data || !blockResponse.data[key]) {
        throw new Error(`无法获取${key}的配置数据`);
      }

      const pageData: PageData = JSON.parse(blockResponse.data[key].json_value);

      // 2. 获取SEO相关数据
      const seoData = pageData?.seo_data;

      // 3. 获取商品列表模块数据
      const listPart = pageData?.product_list;

      if (!listPart || !listPart.id) {
        // 如果没有商品列表配置，只返回页面数据
        setData({
          pageData,
          renderInServer: false,
          seoInfo: null,
          seoData,
          loading: false,
          error: null,
        });
        return;
      }

      // 4. 构建商品列表API参数
      const apiParams: IProductListApiParams = {
        currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
        group_id: listPart.id,
        limit: 36,
        page: 1,
        in_stock: 1,
        is_sale: 1,
        show_adv: 1,
        append_correlation: 1,
      };

      // 5. 获取商品列表数据
      const goodsListResponse = await fetchGoodsList(apiParams);

      if (goodsListResponse.code !== 200) {
        throw new Error('获取商品列表失败');
      }

      const { data: goodsListData } = goodsListResponse;
      const { group = {}, list = [] } = goodsListData as unknown as IProductListApiResponse;

      // 6. 处理SEO所需要的数据
      const seoInfo = handleGoodsListSeo({
        group,
        list,
        id: listPart.id.toString(),
      });

      // 7. 返回完整数据
      setData({
        pageData,
        renderInServer: false,
        seoInfo,
        seoData,
        loading: false,
        error: null,
      });
    } catch (error) {
      setData((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : '获取数据失败',
      }));
    }
  }, [key, currency?.name]);

  // 当key或currency变化时重新获取数据（如果没有服务端数据）
  useEffect(() => {
    // 如果有服务端数据，跳过客户端请求
    if (serverData) {
      return;
    }
    fetchInitialData();
  }, [fetchInitialData, serverData]);

  // 提供手动刷新数据的方法
  const refetch = useCallback(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return {
    ...data,
    refetch,
  };
}
